import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import GradeFaixas from './GradeFaixas';

const inputMoeda = (valor, aoMudar, disabled) => (
  <CurrencyInput
    value={valor}
    decimalSeparator=","
    thousandSeparator="."
    prefix="R$ "
    className="form-control"
    disabled={disabled}
    onChangeEvent={(event, maskedValue, floatValue) => aoMudar(floatValue)}
  />
);

/**
 * FiltrosSimulacao — período (obrigatório), dias mínimos, lookups cobrador/empreendimento (opcionais),
 * radios Analítico/Sintético + %/Valor + Faixa/Valor, grade de faixas editável, valores fixos RFG/RES/RFP.
 * UX 1:1 com USimulaCobrador.dfm.
 */
const FiltrosSimulacao = ({
  cobradores = [],
  empreendimentos = [],
  filtros,
  setFiltros,
  onSimular,
  onCancelar,
  loading = false,
}) => {
  const opcoesCobrador = cobradores.map(c => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta}`,
  }));
  const opcoesEmpreendimento = empreendimentos.map(e => ({
    value: e.codigo_familia,
    label: e.nome_da_familia,
  }));

  const atualizar = (campo, valor) =>
    setFiltros({ ...filtros, [campo]: valor });

  const podeSimular = !!filtros.data_inicio && !!filtros.data_fim;

  // Rótulo da coluna de valor da faixa muda com o modo de comissão.
  const rotuloValorFaixa = filtros.comissao_por_percentual
    ? '% Comissão'
    : 'Valor Comissão';

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        if (podeSimular) onSimular();
      }}
    >
      <Row>
        <Col xs="12" md="3">
          <Form.Group>
            <Form.Label>Período Início *</Form.Label>
            <Form.Control
              type="date"
              value={filtros.data_inicio || ''}
              onChange={e => atualizar('data_inicio', e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col xs="12" md="3">
          <Form.Group>
            <Form.Label>Período Fim *</Form.Label>
            <Form.Control
              type="date"
              value={filtros.data_fim || ''}
              onChange={e => atualizar('data_fim', e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col xs="12" md="3">
          <Form.Group>
            <Form.Label>Dias mín. vencimento</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={filtros.dias}
              onChange={e =>
                atualizar('dias', Math.max(0, parseInt(e.target.value, 10) || 0))
              }
              disabled={loading}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col xs="12" md="4">
          <Form.Group>
            <Form.Label>Cobrador</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesCobrador}
              value={
                opcoesCobrador.find(o => o.value === filtros.cobrador_id) || null
              }
              onChange={opt => atualizar('cobrador_id', opt ? opt.value : null)}
              isClearable
              isDisabled={loading}
              placeholder="Todos os cobradores..."
              noOptionsMessage={() => 'Nenhum cobrador'}
            />
          </Form.Group>
        </Col>
        <Col xs="12" md="4">
          <Form.Group>
            <Form.Label>Empreendimento</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesEmpreendimento}
              value={
                opcoesEmpreendimento.find(
                  o => o.value === filtros.familia_id
                ) || null
              }
              onChange={opt => atualizar('familia_id', opt ? opt.value : null)}
              isClearable
              isDisabled={loading}
              placeholder="Todos os empreendimentos..."
              noOptionsMessage={() => 'Nenhum empreendimento'}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col xs="12" md="4">
          <Form.Label>Relatório</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Analítico"
              name="modoRelatorio"
              checked={filtros.analitico === true}
              onChange={() => atualizar('analitico', true)}
              disabled={loading}
            />
            <Form.Check
              inline
              type="radio"
              label="Sintético"
              name="modoRelatorio"
              checked={filtros.analitico === false}
              onChange={() => atualizar('analitico', false)}
              disabled={loading}
            />
          </div>
        </Col>
        <Col xs="12" md="4">
          <Form.Label>Comissão</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="% Comissão"
              name="modoComissao"
              checked={filtros.comissao_por_percentual === true}
              onChange={() => atualizar('comissao_por_percentual', true)}
              disabled={loading}
            />
            <Form.Check
              inline
              type="radio"
              label="Valor Comissão"
              name="modoComissao"
              checked={filtros.comissao_por_percentual === false}
              onChange={() => atualizar('comissao_por_percentual', false)}
              disabled={loading}
            />
          </div>
        </Col>
        <Col xs="12" md="4">
          <Form.Label>Cálculo</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Por Faixa"
              name="modoCalculo"
              checked={filtros.calcular_por_faixa === true}
              onChange={() => atualizar('calcular_por_faixa', true)}
              disabled={loading}
            />
            <Form.Check
              inline
              type="radio"
              label="Por Valor Fixo"
              name="modoCalculo"
              checked={filtros.calcular_por_faixa === false}
              onChange={() => atualizar('calcular_por_faixa', false)}
              disabled={loading}
            />
          </div>
        </Col>
      </Row>

      {filtros.calcular_por_faixa ? (
        <Row className="mt-3">
          <Col xs="12">
            <Form.Label>Faixas progressivas</Form.Label>
            <GradeFaixas
              faixas={filtros.faixas}
              setFaixas={novas => atualizar('faixas', novas)}
              rotuloValor={rotuloValorFaixa}
              disabled={loading}
            />
          </Col>
        </Row>
      ) : null}

      <Row className="mt-2">
        <Col xs="12" md="4">
          <Form.Group>
            <Form.Label>Valor Fixo RFG</Form.Label>
            {inputMoeda(
              filtros.valor_fixo_rfg,
              v => atualizar('valor_fixo_rfg', v),
              loading
            )}
          </Form.Group>
        </Col>
        <Col xs="12" md="4">
          <Form.Group>
            <Form.Label>Valor Fixo RES</Form.Label>
            {inputMoeda(
              filtros.valor_fixo_res,
              v => atualizar('valor_fixo_res', v),
              loading
            )}
          </Form.Group>
        </Col>
        <Col xs="12" md="4">
          <Form.Group>
            <Form.Label>Valor Fixo RFP</Form.Label>
            {inputMoeda(
              filtros.valor_fixo_rfp,
              v => atualizar('valor_fixo_rfp', v),
              loading
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs="12" className="d-flex" style={{ gap: '0.5rem' }}>
          <Button
            variant="outline-primary"
            type="submit"
            disabled={!podeSimular || loading}
          >
            <FaSearch /> <span className="ms-1">Simular</span>
          </Button>
          <Button
            variant="outline-secondary"
            type="button"
            onClick={onCancelar}
            disabled={loading}
          >
            <FaTimes /> <span className="ms-1">Cancelar</span>
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default FiltrosSimulacao;
